// src/pages/batch/components/AdvancedFilterDrawer.jsx

import React, { useMemo } from "react";
import {
  Drawer as AntDrawer,
  Space,
  Button,
  Row,
  Col,
  Typography,
  Select,
  Input,
  InputNumber,
  Radio,
  Divider,
  Tooltip,
  Tag,
} from "antd";
import {
  DeleteOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";

const { Text, Title } = Typography;

const OPS_BOOLEAN = [
  { value: "is_true", label: "is True" },
  { value: "is_false", label: "is False" },
];
const NO_VALUE_OPS = new Set([
  "exists",
  "not_exists",
  "is_passed",
  "is_failed",
  "is_true",
  "is_false",
]);

const OPS_PASSED_FAILED = [
    { value: "is_passed", label: "is passed" },
    { value: "is_failed", label: "is failed" },
];

const LOCKED_CHILD_PARENTS = new Set(["molecule_id", "reward"]);
const LOCKED_OPERATOR_PARENTS = new Set(["molecule_id"]);


const capitalize = (s) =>
  (s && s.replace(/\b\w/g, (l) => l.toUpperCase())) || "";

const FieldLabel = ({ children, tooltip }) => (
  <Space size={4}>
    <Text strong>{children}</Text>
    {tooltip && (
      <Tooltip title={tooltip}>
        <InfoCircleOutlined style={{ color: "rgba(0,0,0,.45)" }} />
      </Tooltip>
    )}
  </Space>
);

const OP_LABEL = {
  eq: "=",
  neq: "!=",
  gt: ">",
  gte: "\u2265",
  lt: "<",
  lte: "\u2264",
  contains: "contains",
  is: "is",
  is_passed: "Is Passed",
  is_failed: "Is Failed",
  is_true: "Is True",
  is_false: "Is False",
};

const PARENT_COLOR = {
  all: "default",
  molprops: "green",
  glide: "volcano",
  phase: "geekblue",
  adme: "purple",
  molecule_id: "cyan",
  reward: "gold",
};

// ✅ FIX: Updated to look up human-readable labels for parents and children.
const ruleToChipText = (rule, parentLabels, childOptionsByParent) => {
  const parentLabel =
    parentLabels[rule.parent] || capitalize(rule.parent || "all");
  const childOptions = childOptionsByParent[rule.parent] || [];
  const childLabel =
    childOptions.find((opt) => opt.value === rule.child)?.label ||
    capitalize(rule.child || "");

  const op = OP_LABEL[rule.op] || rule.op;

  if (NO_VALUE_OPS.has(rule.op)) {
    return `${parentLabel}: ${childLabel || "value"} ${op}`;
  }

  const valueText = Array.isArray(rule.value)
    ? rule.value.join(" ~ ")
    : rule.value;
  return `${parentLabel}: ${childLabel} ${op} ${valueText}`;
};

const coerceForType = (raw, type) => {
  if (type === "number") {
    if (raw === "" || raw === null || raw === undefined) return undefined;
    const n = Number(raw);
    return Number.isFinite(n) ? n : undefined;
  }
  if (type === "boolean") {
    if (raw === true || raw === false) return raw;
    const s = String(raw).toLowerCase();
    if (s === "true") return true;
    if (s === "false") return false;
    return undefined;
  }
  const textValue = raw == null ? "" : String(raw);
  return textValue.trim();
};

const defaultOpForType = (type, parent, child) => {
  if (parent === "molecule_id") return "contains";
  if (parent === "glide" && /^glide::file::/.test(child)) return "is_passed";
  if (parent === "phase" && /^phase::file::/.test(child)) return "is_passed";
  if (type === "number") return "gte";
  if (type === "boolean") return "is_true";
  return "contains";
};

const ValueControl = ({ rule, onChange, getChildType, valueMetaByChild }) => {
  const child = rule.child || "";
  const childMeta = valueMetaByChild?.[child] || {};
  const t = getChildType(child);

  if (NO_VALUE_OPS.has(rule.op))
    return <Input disabled placeholder="No value needed" />;

  // This logic is now simplified because 'is_true'/'is_false' don't render a value input.
  // The original `BOOL_OPS` check is for a different UI pattern we are not using.
  if (t === "number") {
    const min = Number.isFinite(childMeta.min) ? childMeta.min : undefined;
    const max = Number.isFinite(childMeta.max) ? childMeta.max : undefined;
    const step = Number.isFinite(childMeta.step) ? childMeta.step : 0.01;
    return (
      <InputNumber
        style={{ width: "100%" }}
        placeholder="Enter number"
        value={typeof rule.value === "number" ? rule.value : null}
        onChange={(val) => onChange(coerceForType(val, "number"))}
        min={min}
        max={max}
        step={step}
      />
    );
  }

  return (
    <Input
      placeholder="Enter value"
      value={rule.value ?? ""}
      onChange={(e) => onChange(coerceForType(e.target.value, "text"))}
      allowClear
    />
  );
};

export const AdvancedFilterDrawer = ({
  open,
  onClose,
  rulesDraft,
  setRulesDraft,
  setRulesApplied,
  childOptionsByParent,
  getChildType,
  OPS_NUMERIC,
  OPS_TEXT,
  PARENT_LABELS,
  onApply,
  onClear,
  valueMetaByChild,
  onClearRealtime,
  onDeleteRealtime,
}) => {
 const getDefaultChildForParent = (parent) => {
    const explicit = {
      molecule_id: "molecule_id::molecule_id",
      reward: "reward::reward",
    };
    if (explicit[parent]) return explicit[parent];
    const options = childOptionsByParent[parent] || [];
    return options.length ? options[0].value : undefined;
  };

 const addRule = () =>
    setRulesDraft((r) => {
      const parent = "molprops";
      const child = getDefaultChildForParent(parent);
      const op = defaultOpForType(getChildType(child), parent, child);
      return [
        ...r,
        {
          parent,
          child,
          op,
          value: undefined,
          glue: r.length ? "and" : undefined,
        },
      ];
    });

  const updateRule = (idx, patch) => {
    setRulesDraft((prev) => {
      const next = [...prev];
      const before = next[idx] || {};
      const parentChanged = patch.parent !== undefined && patch.parent !== before.parent;
      const childChanged = patch.child !== undefined && patch.child !== before.child;
      next[idx] = { ...before, ...patch };
      const opChanged = patch.op !== undefined && patch.op !== before.op;

      if (parentChanged) {
        // ✅ FIX 2: Correctly reset the child based on the new parent
        const p = next[idx].parent;
        // The child list will be from childOptionsByParent[p]
        // Set default child
        const defaultChild = getDefaultChildForParent(p);
        next[idx].child = defaultChild;
        const t = getChildType(defaultChild);
        next[idx].op = defaultOpForType(t, p, defaultChild);
        next[idx].value = undefined;
      }
      
      if (childChanged) {
        const p = next[idx].parent;
        const t = getChildType(next[idx].child);
        next[idx].op = defaultOpForType(t, p, next[idx].child);
        next[idx].value = undefined;
      }
      
      if (opChanged && NO_VALUE_OPS.has(patch.op)) {
          next[idx].value = undefined;
      }
      return next;
    });
  };

  const parentOptions = [
    ...Object.entries(PARENT_LABELS).map(([value, label]) => ({ value, label })),
  ];

  const operatorsForRule = (rule) => {
      const { parent, child } = rule || {};
      if (parent === "molecule_id") {
        return [{ value: "contains", label: "contains" }];
      }

    // ✅ FIX 3: Corrected Glide operator list to prevent duplication
    const isGlideFile = parent === "glide" && /^glide::file::/.test(child);
    if (isGlideFile)
      return [ ...OPS_PASSED_FAILED, ...OPS_NUMERIC ]; // Only show pass/fail and numeric ops

    const isPhaseFile = parent === "phase" && /^phase::file::/.test(child);
    if (isPhaseFile)
      return [ ...OPS_PASSED_FAILED, ...OPS_TEXT ]; // Only show pass/fail and text ops

    const t = getChildType(child);
    if (t === "number") return OPS_NUMERIC;
    if (t === "boolean") return OPS_BOOLEAN;

    return OPS_TEXT;
  };

  const isApplyDisabled = useMemo(() => {
    // The button is disabled if ANY rule that requires a value has an empty one.
    return rulesDraft.some(rule => {
      const needsValue = !NO_VALUE_OPS.has(rule.op);
      const hasNoValue = rule.value === null || rule.value === undefined || rule.value === "";
      return needsValue && hasNoValue;
    });
  }, [rulesDraft]);

  return (
    <AntDrawer
      title={
        <Title level={4} style={{ margin: 0 }}>
          Advanced Filter
        </Title>
      }
      placement="right"
      closable={false}
      onClose={onClose}
      open={open}
      width={520}
      bodyStyle={{ paddingBottom: "70px" }}
      footer={
        <Row justify="end">
          <Space>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              type="primary"
              onClick={() => {
                console.log("Advanced filters applied:", JSON.stringify(rulesDraft || []));
                setRulesApplied(rulesDraft);
                onApply?.();
              }}
              disabled={isApplyDisabled}
            >
              Apply
            </Button>
          </Space>
        </Row>
      }
    >
      {rulesDraft.length > 0 && (
        <Space wrap style={{ marginBottom: 12 }}>
          {rulesDraft.length > 0 && (
            <Space wrap align="center" style={{ marginBottom: 12 }}>
              {rulesDraft.map((r, i) => (
                <React.Fragment key={r.id}>
                  {i > 0 && <Text strong>{capitalize(r.glue || "and")}</Text>}
                  <Tag
                    color={PARENT_COLOR[r.parent || "all"]}
                    style={{
                      height: "auto",
                      whiteSpace: "normal",
                      lineHeight: "1.4",
                      maxWidth: "100%",
                    }}
                  >
                    {ruleToChipText(r, PARENT_LABELS, childOptionsByParent)}
                  </Tag>
                </React.Fragment>
              ))}
            </Space>
          )}
        </Space>
      )}

      <Row justify="space-between" align="middle" style={{ marginBottom: 8 }}>
        <Col>
          <Button onClick={addRule}>+ Add Rule</Button>
        </Col>
        <Col>
          <Button onClick={onClearRealtime}>Clear All</Button>
        </Col>
      </Row>
      <Divider style={{ marginTop: 8 }} />

      {rulesDraft.length === 0 ? (
        <Text type="secondary">No rules added.</Text>
      ) : (
        rulesDraft.map((rule, idx) => (
          <div key={rule.id}>
            {idx > 0 && (
              <Space style={{ marginBottom: 8 }}>
                <Radio.Group
                  size="small"
                  value={rule.glue || "and"}
                  onChange={(e) => updateRule(idx, { glue: e.target.value })}
                >
                  <Radio.Button value="and">AND</Radio.Button>
                  <Radio.Button value="or">OR</Radio.Button>
                </Radio.Group>
              </Space>
            )}
            <Row gutter={8} align="middle" style={{ marginBottom: 6 }}>
              <Col span={8}>
                <FieldLabel>Parent</FieldLabel>
                <Select
                  value={rule.parent}
                  style={{ width: "100%", marginTop: 4 }}
                  options={parentOptions}
                  onChange={(p) => updateRule(idx, { parent: p })}
                />
              </Col>
              <Col span={16}>
                <FieldLabel>Child</FieldLabel>
                <Select
                  value={rule.child}
                  style={{ width: "100%", marginTop: 4 }}
                  options={childOptionsByParent[rule.parent] || []}
                  showSearch
                  optionFilterProp="label"
                  onChange={(v) => updateRule(idx, { child: v })}
                  disabled={LOCKED_CHILD_PARENTS.has(rule.parent)}
                />
              </Col>
            </Row>
            <Row gutter={8} align="middle">
              <Col span={8}>
                <FieldLabel>Operator</FieldLabel>
                <Select
                  value={rule.op}
                  style={{ width: "100%", marginTop: 4 }}
                  options={operatorsForRule(rule)}
                  onChange={(v) => updateRule(idx, { op: v })}
                  disabled={LOCKED_OPERATOR_PARENTS.has(rule.parent)}
                />
              </Col>
              <Col span={14}>
                <FieldLabel>Value</FieldLabel>
                <div style={{ marginTop: 4 }}>
                  <ValueControl
                    rule={rule}
                    onChange={(v) => updateRule(idx, { value: v })}
                    getChildType={getChildType}
                    valueMetaByChild={valueMetaByChild}
                  />
                </div>
              </Col>
              <Col
                span={2}
                style={{
                  textAlign: "left",
                  paddingTop: 24,
                  paddingRight: "4px",
                }}
              >
                 <Button type="text" icon={<DeleteOutlined />} danger onClick={() => onDeleteRealtime(idx)} />
              </Col>
            </Row>
            {idx < rulesDraft.length - 1 && <Divider />}
          </div>
        ))
      )}
    </AntDrawer>
  );
};
